import { FC, Fragment, useMemo } from 'react';
// i18next
import { Trans, useTranslation } from 'react-i18next';
// Hooks
import useMenu from 'app/hooks/useMenu';
import useToggle from 'app/hooks/useToggle';
import useAccountStatus from 'app/hooks/useAccountStatus';
// Models
import IContact from 'app/models/Contact';
// Types
import ContactTypes from 'app/types/ContactTypes';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectLoading } from 'app/store/Contacts/Contacts.selectors';
// Async
import { deleteContact } from 'app/store/Contacts/Contacts.async';
// Mui
import { TableRow, TableCell, Theme, Divider, Box } from '@mui/material';
// Icon
import {
  MoreVert as MoreVertIcon,
  PersonOutlined as PersonOutlinedIcon,
  BusinessOutlined as BusinessOutlinedIcon
} from '@mui/icons-material';
// Dialogs
import ConfirmationDialog from 'app/dialogs/ConfirmationDialog';
// Components
import { IconButton } from 'app/components/Mui';
// 
import ContactsFormDialog from './ContactsFormDialog';
import ContactDetailsDrawer from './ContactDetailsDrawer';
// Utilities
import { concatAddress } from 'app/utilities/Utilities';
import { formatPhoneNumber } from '@root/utilities/utlities';

type Props = {
  contact: IContact;
}

const ContactsListItem:FC<Props> = ({
  // Props
  contact
}) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const loading = useAppSelector(selectLoading);

  const { isSuspended } = useAccountStatus();

  const { Menu, MenuItem, openMenu } = useMenu();
  const { open:openEditDialog, toggle:toggleEditDialog } = useToggle();
  const { open:openDetailsDialog, toggle:toggleDetailsDialog } = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();

  const handleConfirm = () => {
    dispatch(deleteContact(contact.id));
  }

  const phone = useMemo(() => {
    if ( !contact.phones || !contact.phones.length ) return '';
    const phoneObj = contact.phones[0];
    return `${formatPhoneNumber(phoneObj.phoneNumber)}${phoneObj.extension ? ` ext. ${phoneObj.extension}` : ''}`;
  }, [contact.phones]);

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {contact.type === ContactTypes.Person ? (
              <PersonOutlinedIcon />
            ) : (
              <BusinessOutlinedIcon />
            )}
            {`${contact.name}${contact.referenceContact?.name ? ` (${contact.referenceContact?.name})` : ''}`}
          </Box>
        </TableCell>
        <TableCell sx={{
          '& a': {
            textDecoration: 'none',
            color: theme => theme.palette.primary.main
          }
        }}><a href={`mailto:${contact.email}`} target="_blank" rel="noreferrer">{contact.email || ''}</a></TableCell>
        <TableCell sx={{
          '& a': {
            textDecoration: 'none',
            color: theme => theme.palette.primary.main
          }
        }}><a href={`tel:${contact.phones?.[0]?.phoneNumber}`}>{phone}</a></TableCell>
        <TableCell>{concatAddress(contact.address)}</TableCell>
        <TableCell align="right">
          <IconButton
            name={`Contact dropdown ${contact.id}`}
            aria-label="Contact item dropdown button"
            onClick={openMenu}
          ><MoreVertIcon /></IconButton>
          <Menu>
            <MenuItem
              name={`Details contact ${contact.id}`}
              onClick={toggleDetailsDialog}
            >{t('buttons.details')}</MenuItem>
            <Divider />
            <MenuItem
              name={`Edit contact ${contact.id}`}
              onClick={toggleEditDialog}
              disabled={isSuspended}
            >{t('buttons.edit')}</MenuItem>
            <MenuItem
              name={`Delete contact ${contact.id}`}
              sx={{
                color: (theme:Theme) => theme.palette.error.main
              }}
              onClick={toggleConfirmationDialog}
            >{t('buttons.delete')}</MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      {/* Dialogs */}
      {openEditDialog ? (
        <ContactsFormDialog
          open={openEditDialog}
          onClose={toggleEditDialog}
          contact={contact}
        />
      ) : null}
      {openDetailsDialog ? (
        <ContactDetailsDrawer
          open={openDetailsDialog}
          onClose={toggleDetailsDialog}
          contactId={contact.id}
        />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          loading={loading}
          title={t('dialogs.confirmation.deleteTitle', { dataType: t('labels.contact') })}
          content={(
            <Trans
              t={t} i18nKey="dialogs.confirmation.deleteConfirmationWithName"
              components={{ strong: <strong /> }}
              values={{ dataType: t('labels.contact'), dataName: contact.name }}
            />
          )}
          onConfirm={handleConfirm}
        />
      ) : null}
    </Fragment>
  )
}

export default ContactsListItem;
